import type { CompetitionElementsMode } from '@alison/analytics';
import { AnalyticsService } from '@alison/analytics/client';
import GpsFixedOutlined from '@mui/icons-material/GpsFixedOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { ExportCsvButton } from '../../creatives/ExportCsvButton';
import { TOGGLE_BUTTON_SX } from '../../creatives/utils';
import type { CompetitionFilterKey } from '../../filters/competitionFilterCatalog';
import { CompetitionElementsCategoryRow } from './CompetitionElementsCategoryRow';
import { CompetitionElementsCoveragePanel } from './CompetitionElementsCoveragePanel';
import { CompetitionElementsTableGrid } from './CompetitionElementsTableGrid';
import {
  type CompetitionElementRow,
  type CompetitionElementsData,
  type CompetitionElementsScope,
  type ElementStatus,
  useCompetitionElements,
  useCompetitionElementsMostUsed,
} from './data/api';
import { competitionElementsToCsv, downloadCsv } from './data/csv';
import {
  filterRowsByCategory,
  filterRowsByScene,
  filterRowsByStatus,
  groupCompetitionElementRows,
  type Scene,
} from './data/tree';

/** Matches the design mock and the `elementCategory` token order in `libs/ui/src/theme/tokens.ts` —
 * the backend's `categoriesOf()` sorts alphabetically, so this reorders for display only. */
const CATEGORY_DISPLAY_ORDER = ['Product', 'Structure', 'Story', 'Graphics', 'Conceptual'];

interface CompetitionElementsBoardProps {
  accountIds: number[];
  dateWindow: [string, string];
  filters: Partial<Record<CompetitionFilterKey, string[]>>;
  /** Opens the Creatives tab filtered to a feature row's element, or a tag row's element + tag. */
  onOpenCreatives: (feature: string, tag: string | null) => void;
}

function toggleInArray<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

function sortCategoriesByTaxonomy(
  categories: CompetitionElementsData['categories'],
): CompetitionElementsData['categories'] {
  const rankOf = (category: string): number => {
    const index = CATEGORY_DISPLAY_ORDER.indexOf(category);
    return index === -1 ? CATEGORY_DISPLAY_ORDER.length : index;
  };
  return categories.toSorted((left, right) => {
    const byRank = rankOf(left.category) - rankOf(right.category);
    return byRank !== 0 ? byRank : left.category.localeCompare(right.category);
  });
}

export function CompetitionElementsBoard({
  accountIds,
  dateWindow,
  filters,
  onOpenCreatives,
}: CompetitionElementsBoardProps): ReactNode {
  const [mode, setMode] = useState<CompetitionElementsMode>('all');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<ElementStatus[]>([]);
  const [activeScenes, setActiveScenes] = useState<Scene[]>([]);

  const scope = useMemo<CompetitionElementsScope>(
    () => ({
      dateRange: dateWindow,
      ...(accountIds.length ? { accountIds } : {}),
      ...(Object.keys(filters).length ? { filters } : {}),
    }),
    [accountIds, dateWindow, filters],
  );

  const allElements = useCompetitionElements(scope, mode === 'all' && accountIds.length > 0);
  const mostUsedElements = useCompetitionElementsMostUsed(
    scope,
    mode === 'most-used' && accountIds.length > 0,
  );
  const active = mode === 'all' ? allElements : mostUsedElements;
  const pending = active.isPending && accountIds.length > 0;

  function toggleCategory(category: string): void {
    AnalyticsService.track('Competition Elements - Category Toggled', {
      category,
      selected: !selectedCategories.includes(category),
    });
    setSelectedCategories((current) => toggleInArray(current, category));
  }

  function toggleStatus(status: ElementStatus): void {
    AnalyticsService.track('Competition Elements - Status Toggled', {
      status,
      selected: !selectedStatuses.includes(status),
    });
    setSelectedStatuses((current) => toggleInArray(current, status));
  }

  function toggleScene(scene: Scene): void {
    AnalyticsService.track('Competition Elements - Scene Toggled', {
      scene,
      active: !activeScenes.includes(scene),
    });
    setActiveScenes((current) => toggleInArray(current, scene));
  }

  function changeMode(next: CompetitionElementsMode): void {
    if (mode === next) return;
    AnalyticsService.track('Competition Elements - Mode Changed', { mode: next });
    setMode(next);
  }

  const visibleRows = useMemo(() => {
    if (active.data === undefined) return [];
    const byCategory = filterRowsByCategory(active.data.rows, selectedCategories);
    const byStatus = filterRowsByStatus(byCategory, selectedStatuses);
    const byScene = filterRowsByScene(byStatus, activeScenes);
    return groupCompetitionElementRows(byScene, active.data.total);
  }, [active.data, selectedCategories, selectedStatuses, activeScenes]);

  const openCreatives = useCallback(
    (row: CompetitionElementRow): void => {
      const tag = row.rowLevel === 'tag' ? row.tag : null;
      AnalyticsService.track('Competition Elements - Creatives Opened', {
        feature: row.feature,
        tag,
      });
      onOpenCreatives(row.feature, tag);
    },
    [onOpenCreatives],
  );

  function exportCsv(): void {
    if (active.data === undefined) return;
    AnalyticsService.track('Competition Elements - CSV Exported');
    downloadCsv(
      `competition_elements_${dateWindow[0]}_${dateWindow[1]}.csv`,
      competitionElementsToCsv(visibleRows, active.data.competitorsById),
    );
  }

  return (
    <Box sx={{ px: 6, py: 4 }}>
      {active.error ? (
        <Alert severity="error">{active.error.message}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <CompetitionElementsCategoryRow
            categories={active.data ? sortCategoriesByTaxonomy(active.data.categories) : []}
            loading={pending}
            selected={selectedCategories}
            onToggle={toggleCategory}
          />
          <CompetitionElementsCoveragePanel
            summary={active.data?.summary}
            loading={pending}
            selected={selectedStatuses}
            onToggle={toggleStatus}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <ToggleButtonGroup
              value={[mode, ...activeScenes]}
              sx={{ backgroundColor: 'background.page' }}
            >
              <ToggleButton value="all" sx={TOGGLE_BUTTON_SX} onClick={() => changeMode('all')}>
                All
              </ToggleButton>
              <ToggleButton
                value="most-used"
                sx={{ gap: 1, ...TOGGLE_BUTTON_SX }}
                onClick={() => changeMode('most-used')}
              >
                <GpsFixedOutlined sx={{ fontSize: 14 }} />
                Most Used By Competitor
              </ToggleButton>
              <ToggleButton
                value="hook-scene"
                sx={TOGGLE_BUTTON_SX}
                onClick={() => toggleScene('hook-scene')}
              >
                Hook Scene
              </ToggleButton>
              <ToggleButton
                value="end-card"
                sx={TOGGLE_BUTTON_SX}
                onClick={() => toggleScene('end-card')}
              >
                End Card
              </ToggleButton>
            </ToggleButtonGroup>
            <ExportCsvButton
              dataTest="competition-elements-export-csv"
              disabled={active.data === undefined}
              onClick={exportCsv}
            />
          </Box>
          <CompetitionElementsTableGrid
            rows={visibleRows}
            competitorsById={active.data?.competitorsById ?? {}}
            loading={pending}
            expandTags={selectedStatuses.length > 0}
            onOpenCreatives={openCreatives}
          />
        </Box>
      )}
    </Box>
  );
}
